import Invoice from "../models/Invoice";
import { InvoiceBatchExportSummary } from "../models/InvoiceBatch";

/**
 * One follow-up item on an invoice of a processed batch:
 * - `FAILED` — not created in the invoice system, time reports released.
 * - `HELD` — may exist in the invoice system, time reports held until an admin
 *   says whether it does ("Kontrollera i Visma").
 * - `ATTACHMENT_MISSING` — created, but the specification PDF is not attached.
 */
export type InvoiceExportIssueKind = "FAILED" | "HELD" | "ATTACHMENT_MISSING";

export interface InvoiceExportIssue {
	kind: InvoiceExportIssueKind;
	reason: string;
	/** Absent while the item is open. */
	resolved?: {
		at: string;
		by: string;
		outcome?: "RELEASED" | "CONFIRMED_EXPORTED";
	};
}

type IssueSource = Pick<Invoice, "exportError" | "attachmentMissing">;

export function getInvoiceExportIssues(invoice: IssueSource): InvoiceExportIssue[] {
	const issues: InvoiceExportIssue[] = [];

	if (invoice.exportError) {
		issues.push({
			kind: invoice.exportError.holdTimeReports ? "HELD" : "FAILED",
			reason: invoice.exportError.reason,
			resolved: invoice.exportError.resolved,
		});
	}

	if (invoice.attachmentMissing) {
		issues.push({
			kind: "ATTACHMENT_MISSING",
			reason: invoice.attachmentMissing.reason,
			resolved: invoice.attachmentMissing.resolved,
		});
	}

	return issues;
}

/**
 * Open items per kind, counted from the invoices themselves. Same semantics as
 * the batch's `exportSummary` (resolved items drop out), but always current on
 * the detail page right after an action, without waiting for the summary.
 */
export function countOpenExportIssues(invoices: IssueSource[]): InvoiceBatchExportSummary {
	const summary: InvoiceBatchExportSummary = { failed: 0, held: 0, attachmentMissing: 0 };

	for (const invoice of invoices) {
		for (const issue of getInvoiceExportIssues(invoice)) {
			if (issue.resolved) continue;

			if (issue.kind === "FAILED") summary.failed++;
			else if (issue.kind === "HELD") summary.held++;
			else summary.attachmentMissing++;
		}
	}

	return summary;
}

export function hasOpenExportIssues(summary?: InvoiceBatchExportSummary): boolean {
	return !!summary && summary.failed + summary.held + summary.attachmentMissing > 0;
}

/**
 * Whether any invoice's time reports were released by a failed export — then a
 * new batch with the same selection picks them up. True for clean failures and
 * for held invoices an admin marked as not existing in the invoice system.
 */
export function hasReleasedTimeReports(invoices: IssueSource[]): boolean {
	return invoices.some((invoice) =>
		getInvoiceExportIssues(invoice).some(
			(issue) =>
				issue.kind === "FAILED" || (issue.kind === "HELD" && issue.resolved?.outcome === "RELEASED")
		)
	);
}

/**
 * "2 misslyckade · 1 att kontrollera · 1 utan specifikation" — only the
 * non-zero parts; empty string when there is nothing open.
 */
export function describeExportSummary(
	summary: InvoiceBatchExportSummary | undefined,
	t: (key: string, options?: Record<string, unknown>) => string
): string {
	if (!summary) return "";

	return [
		summary.failed > 0 && t("invoiceBatch.exportSummary.failed", { count: summary.failed }),
		summary.held > 0 && t("invoiceBatch.exportSummary.held", { count: summary.held }),
		summary.attachmentMissing > 0 &&
			t("invoiceBatch.exportSummary.attachmentMissing", { count: summary.attachmentMissing }),
	]
		.filter(Boolean)
		.join(" · ");
}

/**
 * The customer's own name when the invoice was loaded with
 * `invoices.customer` expanded, else the persisted billing name.
 */
export function invoiceCustomerName(invoice: Pick<Invoice, "customer" | "invoiceName">): string {
	return (invoice.customer as { name?: string })?.name || invoice.invoiceName;
}

/**
 * Message for a failed invoice batch rollback. A Visma.net batch past preview
 * answers 400 `ROLLBACK_NOT_SUPPORTED`: its invoices exist in Visma and Origo
 * never deletes them there.
 */
export function invoiceBatchRollbackErrorKey(error: unknown) {
	return (error as { error?: { code?: string } })?.error?.code === "ROLLBACK_NOT_SUPPORTED"
		? "invoiceBatch.rollbackNotSupported"
		: "invoiceBatch.rollbackFailed";
}
